import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';

export type ConfirmDialogType = 'success' | 'info' | 'warning' | 'danger';

@Component({
  selector: 'app-confirm-dialog',
  imports: [NgClass],
  templateUrl: './confirm-dialog.html',
  styleUrl: './confirm-dialog.scss',
})
export class ConfirmDialog implements OnDestroy {
  private readonly translateService = inject(TranslateService);
  private autoCloseSubscription?: Subscription;

  readonly title = signal<string>('');
  readonly message = signal<string>('');
  readonly details = signal<string | null>(null);
  readonly confirmText = signal<string>('');
  readonly cancelText = signal<string>('');
  readonly dialogMode = signal<boolean>(false);
  readonly type = signal<ConfirmDialogType>('info');
  readonly autoCloseMillis = signal<number>(5000);

  readonly typeClass = computed(() => `dialog-type-${this.type()}`);
  readonly iconClass = computed(() => {
    switch (this.type()) {
      case 'success':
        return 'fa-solid fa-circle-check';
      case 'warning':
        return 'fa-solid fa-triangle-exclamation';
      case 'danger':
        return 'fa-solid fa-circle-exclamation';
      case 'info':
      default:
        return 'fa-solid fa-circle-info';
    }
  });

  constructor(public activeModal: NgbActiveModal) {
    this.translateService
      .stream([
        'components.organisms.dialog.title',
        'components.organisms.dialog.message',
        'components.organisms.dialog.confirm',
        'components.organisms.dialog.cancel',
      ])
      .subscribe((values: Record<string, string>) => {
        this.title.set(values['components.organisms.dialog.title']);
        this.message.set(values['components.organisms.dialog.message']);
        this.confirmText.set(values['components.organisms.dialog.confirm']);
        this.cancelText.set(values['components.organisms.dialog.cancel']);
      });
  }

  ngOnDestroy(): void {
    this.clearAutoClose();
  }

  setTitle(title: string): void {
    this.title.set(title);
  }

  setMessage(message: string): void {
    this.message.set(message);
  }

  setDetails(details: string | null): void {
    this.details.set(details);
  }

  setConfirmText(confirmText: string): void {
    this.confirmText.set(confirmText);
  }

  setCancelText(cancelText: string): void {
    this.cancelText.set(cancelText);
  }

  setDialogMode(dialogMode: boolean): void {
    this.dialogMode.set(dialogMode);
    this.configureAutoClose();
  }

  setType(type: ConfirmDialogType): void {
    this.type.set(type);
  }

  setAutoCloseMillis(autoCloseMillis: number): void {
    this.autoCloseMillis.set(autoCloseMillis);
    this.configureAutoClose();
  }

  private configureAutoClose(): void {
    this.clearAutoClose();

    if (!this.dialogMode()) {
      return;
    }

    const timeout = this.autoCloseMillis();
    if (timeout <= 0) {
      return;
    }

    this.autoCloseSubscription = timer(timeout).subscribe(() => {
      this.activeModal.close(true);
    });
  }

  private clearAutoClose(): void {
    this.autoCloseSubscription?.unsubscribe();
    this.autoCloseSubscription = undefined;
  }
}
